import { formatDate } from '@angular/common'
import { Component, Input, type OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import {
  BdcAdvanceCancellationBeforeCheckInEnum,
  BdcBookingModelType,
  BdcCancellationGracePeriodEnum,
  BdcContentV2Service,
  BdcDamageCollectionMode,
  BdcDamagePolicyCollectionWhen,
  BdcDamagePolicyReturnWhen,
  BdcDamagePolicyType,
  BdcNotificationChannel,
  BdcPaymentTypeCodes,
  BdcPetsAllowedEnum,
  BdcPetsPriceType,
  BdcPropertyDetails,
  BdcPropertyPricingType,
  BdcPropertySettingsAndPolicies,
  BdcTaxPayerNumberType,
  getBdcTimeOptions,
} from '@tokeet-frontend/channel-content'
import {
  ActionFailed,
  compactObject,
  Destroyable,
  positive,
  SaveForm,
  Toaster,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { finalize, startWith } from 'rxjs/operators'

@Component({
  selector: 'app-bdc-property-settings',
  templateUrl: './bdc-property-settings.component.html',
  styleUrls: ['./bdc-property-settings.component.scss'],
})
export class BdcPropertySettingsComponent extends Destroyable implements OnInit {
  @Input() propertyId: number
  @Input() property: BdcPropertyDetails

  frenchTaxDetailsForm = this.fb.group({
    declare_revenue: [],
    has_vat: [],
    registered_in_rcs: [],
  })

  isDamagePolicyEnabledCtrl = new FormControl(false)
  damagePolicyForm = this.fb.group({
    policy_type: [BdcDamagePolicyType.NONE],
    amount: [undefined, [Validators.min(0)]],
    damage_programme_terms_agreed: [false],
    collection_mode: [],
    collection_when: [],
    return_mode: [],
    return_when: [],
  })

  cancellationExceptionsForm = this.fb.group({
    grace_period: this.fb.group({
      enabled: [false],
      after_booking: [],
    }),
    advance_cancellation: this.fb.group({
      enabled: [false],
      before_check_in: [],
    }),
  })

  miscForm = this.fb.group({
    bed_linen_policy: this.fb.group({
      amount: [null, [Validators.min(0)]],
      enabled: [],
    }),
    quiet_hours: this.fb.group({
      enabled: [],
      start: [],
      end: [],
    }),
    renovation: this.fb.group({
      enabled: [],
      start: [],
      end: [],
    }),
    french_tax_details: this.frenchTaxDetailsForm,
    property_settings: this.fb.group({
      require_cvc: [],
      require_booker_address: [],
      require_booker_phone_number: [],
      auto_replenish: [],
      replenish_closed_rooms: [],
      pricing_type: [],
      allow_smoking: [],
      view_cc_details: [],
      curfew: this.fb.group({
        enabled: [],
        start: [],
        end: [],
      }),
      age_restriction: this.fb.group({
        enabled: [],
        min: [null, [Validators.min(18), Validators.max(99)]],
        max: [null, [Validators.min(18), Validators.max(99)]],
      }),
      long_stay: this.fb.group({
        enabled: [],
        max_length_of_stay: [], // 45, 60, 75, 90 days
      }),
    }),
  })

  policyForm = this.fb.group({
    booking_model: this.fb.group({ type: [null, [Validators.required]] }),
    accepted_payment_types: this.fb.group({ codes: [] }),
    pets: this.fb.group({
      pets_allowed: [],
      pets_price_type: [],
    }),
    children_policies: this.fb.group({
      allow_children: [],
      min_age: [null, [Validators.min(0)]],
    }),
  })

  invoiceSettingsForm = this.fb.group({
    legal_name: ['', [Validators.required]],
    contact_person: ['', [Validators.required]],
    address: ['', [Validators.required]],
    country_code: ['', [Validators.required]],
    city: ['', [Validators.required]],
    postal_code: ['', [Validators.required]],
    state: [''],
    notification_channel: [],
    brazil_tax_details: this.fb.group({
      enabled: [],
      tax_payer_number_type: [],
      tax_payer_number: [],
      city_hall_id: ['', [Validators.minLength(8), Validators.maxLength(8)]],
      email: ['', [Validators.email]],
    }),
  })

  brazilCountryCode = 'BR'

  time30MinsOptions = getBdcTimeOptions(30)
  time60MinsOptions = getBdcTimeOptions(60)

  bdcBookingModelType = BdcBookingModelType
  bookingModelOptions = [
    { label: $localize`Instant Booking`, value: BdcBookingModelType.IB },
    { label: $localize`Request to Book`, value: BdcBookingModelType.RTB },
  ]
  paymentTypeOptions = BdcPaymentTypeCodes
  petsAllowedOptions = Object.values(BdcPetsAllowedEnum)
  petsPriceTypeOptions = Object.values(BdcPetsPriceType)
  priceTypeOptions = Object.values(BdcPropertyPricingType)
  longStayOptions = [
    { label: $localize`45 days`, value: 45 },
    { label: $localize`60 days`, value: 60 },
    { label: $localize`75 days`, value: 75 },
    { label: $localize`90 days`, value: 90 },
  ]

  bdcDamagePolicyType = BdcDamagePolicyType
  damagePolicyTypeOptions = [
    { label: $localize`None`, value: BdcDamagePolicyType.NONE },
    { label: $localize`Handled by Property`, value: BdcDamagePolicyType.HANDLED_BY_PROPERTY },
    { label: $localize`Handled by Booking.com`, value: BdcDamagePolicyType.HANDLED_BY_BOOKING_COM },
  ]
  damageCollectionModeOptions = Object.values(BdcDamageCollectionMode)
  damageCollectionWhenOptions = Object.values(BdcDamagePolicyCollectionWhen)
  damageReturnModeOptions = Object.values(BdcDamageCollectionMode)
  damageReturnWhenOptions = Object.values(BdcDamagePolicyReturnWhen)

  cancellationGracePeriodOptions = [
    { label: $localize`1 hours`, value: BdcCancellationGracePeriodEnum.HOURS_1 },
    { label: $localize`4 hours`, value: BdcCancellationGracePeriodEnum.HOURS_4 },
    { label: $localize`24 hours`, value: BdcCancellationGracePeriodEnum.HOURS_24 },
  ]

  cancellationBeforeCheckInOptions = [
    { label: $localize`4 weeks`, value: BdcAdvanceCancellationBeforeCheckInEnum.WEEKS_4 },
    { label: $localize`8 weeks`, value: BdcAdvanceCancellationBeforeCheckInEnum.WEEKS_8 },
    { label: $localize`12 weeks`, value: BdcAdvanceCancellationBeforeCheckInEnum.WEEKS_12 },
  ]

  notificationChannelOptions = Object.values(BdcNotificationChannel)
  taxPayerNumberTypeOptions = [
    { label: $localize`Company`, value: BdcTaxPayerNumberType.CNPJ },
    { label: $localize`Private Individual`, value: BdcTaxPayerNumberType.CPF },
  ]

  isLoading = true

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private toaster: Toaster,
    private contentService: BdcContentV2Service
  ) {
    super()
  }

  ngOnInit(): void {
    const toggleGroup = (
      group: AbstractControl,
      fields: string[],
      toggleField: string | AbstractControl = 'enabled',
      isEnabled: (v: any) => boolean = (v) => !!v
    ) => {
      const toggleCtrl = typeof toggleField === 'string' ? (group.get(toggleField) as FormControl) : toggleField
      toggleCtrl.valueChanges.pipe(startWith(false), untilDestroy(this)).subscribe((value) => {
        fields.forEach((field) => {
          if (isEnabled(value)) {
            group.get(field)?.enable()
            group.get(field)?.addValidators([Validators.required])
          } else {
            group.get(field)?.disable()
            group.get(field)?.removeValidators([Validators.required])
            group.get(field)?.reset()
          }
        })
      })
    }

    toggleGroup(this.policyForm.get('children_policies'), ['min_age'], 'allow_children')
    toggleGroup(this.miscForm.get('bed_linen_policy'), ['amount'])
    toggleGroup(this.miscForm.get('quiet_hours'), ['start', 'end'])
    toggleGroup(this.miscForm.get('renovation'), ['start', 'end'])
    toggleGroup(this.cancellationExceptionsForm.get('grace_period'), ['after_booking'])
    toggleGroup(this.cancellationExceptionsForm.get('advance_cancellation'), ['before_check_in'])
    toggleGroup(this.miscForm.get('property_settings.curfew'), ['start', 'end'])
    toggleGroup(this.miscForm.get('property_settings.age_restriction'), ['min', 'max'])
    toggleGroup(this.miscForm.get('property_settings.long_stay'), ['max_length_of_stay'])
    toggleGroup(
      this.policyForm.get('pets'),
      ['pets_price_type'],
      'pets_allowed',
      (v) => !!v && v != BdcPetsAllowedEnum.PETS_NOT_ALLOWED
    )
    toggleGroup(
      this.invoiceSettingsForm.get('brazil_tax_details'),
      ['tax_payer_number_type', 'tax_payer_number', 'city_hall_id', 'email'],
      this.invoiceSettingsForm.get('country_code'),
      (v) => v === this.brazilCountryCode
    )

    this.isDamagePolicyEnabledCtrl.valueChanges.pipe(untilDestroy(this)).subscribe((value) => {
      if (value) {
        this.damagePolicyForm.get('policy_type').setValue(BdcDamagePolicyType.HANDLED_BY_PROPERTY)
      } else {
        this.damagePolicyForm.get('policy_type').setValue(BdcDamagePolicyType.NONE)
      }
    })

    this.damagePolicyForm
      .get('collection_mode')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((value) => {
        const whenCtrl = this.damagePolicyForm.get('collection_when')
        if (value === BdcDamageCollectionMode.CASH) {
          whenCtrl.setValue(BdcDamagePolicyCollectionWhen.ON_ARRIVAL)
          whenCtrl.disable()
        } else if (whenCtrl.disabled) {
          whenCtrl.enable()
        }
      })

    this.damagePolicyForm
      .get('return_mode')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((value) => {
        const whenCtrl = this.damagePolicyForm.get('return_when')
        if (value === BdcDamageCollectionMode.CASH) {
          whenCtrl.setValue(BdcDamagePolicyReturnWhen.ON_CHECKOUT)
          whenCtrl.disable()
        } else if (whenCtrl.disabled) {
          whenCtrl.enable()
        }
      })

    this.invoiceSettingsForm
      .get('brazil_tax_details.tax_payer_number_type')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((value) => {
        const taxPayerNumberCtrl = this.invoiceSettingsForm.get('brazil_tax_details.tax_payer_number')
        if (value === BdcTaxPayerNumberType.CNPJ) {
          taxPayerNumberCtrl.setValidators([
            Validators.minLength(14),
            Validators.maxLength(14),
            Validators.required,
            Validators.pattern(/^\d+$/),
          ])
        } else if (value === BdcTaxPayerNumberType.CPF) {
          taxPayerNumberCtrl.setValidators([
            Validators.minLength(11),
            Validators.maxLength(11),
            Validators.required,
            Validators.pattern(/^\d+$/),
          ])
        } else {
          taxPayerNumberCtrl.setValidators([Validators.required])
        }
        taxPayerNumberCtrl.updateValueAndValidity()
      })
    this.onRefresh()
  }

  onRefresh() {
    this.contentService
      .getPropertySettings(this.propertyId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          this.policyForm.patchValue({ ...data })
          this.damagePolicyForm.patchValue({ ...data.damage_policy })
          this.cancellationExceptionsForm.patchValue({ ...data.cancellation_exceptions })
          this.miscForm.patchValue({ ...data })
          this.isDamagePolicyEnabledCtrl.setValue(
            data.damage_policy?.policy_type ? data.damage_policy?.policy_type !== BdcDamagePolicyType.NONE : false,
            { emitEvent: false }
          )
          this.invoiceSettingsForm.patchValue({ ...data.invoice_settings })
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  @SaveForm(true)
  onSavePolicy(form: FormGroup) {
    this.save(form.getRawValue())
  }

  @SaveForm(true)
  onSaveDamagePolicy(form: FormGroup) {
    this.save({ damage_policy: form.getRawValue() })
  }

  @SaveForm(true)
  onSaveCancelException(form: FormGroup) {
    this.save({ cancellation_exceptions: form.getRawValue() })
  }

  @SaveForm(true)
  onSaveMisc(form: FormGroup) {
    const { renovation, french_tax_details, ...payload } = form.getRawValue()
    if (renovation.enabled) {
      renovation.start = formatDate(renovation.start, 'yyyy-MM-dd', 'en-GB')
      renovation.end = formatDate(renovation.end, 'yyyy-MM-dd', 'en-GB')
      payload.renovation = renovation
    }
    if (Object.values(french_tax_details).some((v) => !!v)) {
      payload.french_tax_details = french_tax_details
    }
    this.save(payload)
  }

  @SaveForm(true)
  onSaveInvoiceSettings(form: FormGroup) {
    const { brazil_tax_details, ...payload } = form.getRawValue()
    const isBrazil = payload.country_code === this.brazilCountryCode
    this.save({
      invoice_settings: {
        ...payload,
        brazil_tax_details: isBrazil ? brazil_tax_details : undefined,
      },
    })
  }

  private save(payload: BdcPropertySettingsAndPolicies) {
    this.isLoading = true
    this.contentService
      .updatePropertySettings(this.propertyId, compactObject(payload))
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Property settings saved successfully.`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
